import classNames from 'classnames'
import { useFormik } from 'formik'
import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import ModalComponent from '../..'
import {
  useCreateAmbassadorMutation,
  useGetAmbassadorQuery,
  useUpdateAmbassadorMutation,
} from '../../../../redux-toolkits/loyalty/loyalty.slice'
import { useGetAllCustomersQuery } from '../../../../redux-toolkits/user/user.slice'
import { CustomersQuery } from '../../../../redux-toolkits/customers/customers.type'
import {
  AddAmbassadorSchema,
  UpdateAmbassadorSchema,
} from '../../../../validation/validate'
import { AccountDetails } from '../../../accountDetails'
import { Button } from '../../../button'
import ComboBox from '../../../inputs/combobox'
import Spinner from '../../../spinner/Spinner'

type T = {
  id?: string
  name?: string
}

export const AddAmbassadorModal: React.FC<{
  open: boolean
  setOpen: (cal: boolean) => void
  onCloseCallback?: () => void
  onSuccessCallback?: () => void
  ambassadorId?: string
}> = ({
  open,
  setOpen,
  onCloseCallback = () => {},
  onSuccessCallback = () => {},
  ambassadorId,
}) => {
  const [query, setQuery] = useState<CustomersQuery>({
    skip: 0,
    limit: 10,
    search: undefined,
    deleted: undefined,
    startDate: undefined,
    endDate: undefined,
    isCustomDateRange: false,
    filter: 'inception_to_date',
    sort: 'fully_onboarded',
  })

  const [createAmbassador, { isLoading: creatingAmbassador }] =
    useCreateAmbassadorMutation()

  const [updateAmbassador, { isLoading: updatingAmbassador }] =
    useUpdateAmbassadorMutation()

  const formik = useFormik({
    initialValues: {
      customer: {
        id: undefined,
        name: '',
      },
      accountName: '',
      accountNumber: '',
      bankCode: '',
      bankName: '',
    },
    validationSchema: ambassadorId
      ? UpdateAmbassadorSchema
      : AddAmbassadorSchema,
    onSubmit: async (vals, { resetForm }) => {
      if (!ambassadorId) {
        const resp = await createAmbassador({
          user: vals.customer.id!,
        })

        if (resp.data?.success) {
          toast.success(resp.data.message)
          resetForm()
          onSuccessCallback()
        }
      } else {
        const resp = await updateAmbassador({
          id: ambassadorId,
          payload: {
            settlementAccount: {
              accountNumber: vals.accountNumber,
              bankCode: vals.bankCode,
            },
          },
        })

        if (resp.data?.success) {
          toast.success(resp.data.message)
          resetForm()
          onSuccessCallback()
        }
      }
    },
  })

  const {
    values,
    errors,
    handleChange,
    handleBlur,
    touched,
    setFieldValue,
    handleSubmit,
    isValid,
    resetForm,
  } = formik

  const { data: customers, isFetching: fetchingCustomers } =
    useGetAllCustomersQuery(
      { ...query, search: query.search || undefined },
      {
        refetchOnMountOrArgChange: true,
        skip: !open,
      },
    )

  const { data: ambassadorDetails, isFetching: fetchingAmbassadorDetails } =
    useGetAmbassadorQuery(
      {
        id: ambassadorId!,
        refetch: open,
      },
      {
        refetchOnMountOrArgChange: true,
        skip: !ambassadorId || !open,
      },
    )

  useEffect(() => {
    if (
      ambassadorDetails?.data.ambassador &&
      ambassadorId &&
      !fetchingAmbassadorDetails
    ) {
      setFieldValue('customer', {
        id: ambassadorDetails.data.ambassador.user,
        name: ambassadorDetails.data.ambassador.user,
      })
      setFieldValue(
        'accountName',
        ambassadorDetails.data?.settlementAccount?.accountName,
      )
      setFieldValue(
        'accountNumber',
        ambassadorDetails.data?.settlementAccount?.accountNumber,
      )
      setFieldValue(
        'bankCode',
        ambassadorDetails.data?.settlementAccount?.bankCode,
      )
      setFieldValue(
        'bankName',
        ambassadorDetails.data?.settlementAccount?.bankName,
      )
    } else {
      resetForm()
    }
  }, [ambassadorDetails, ambassadorId, fetchingAmbassadorDetails])

  return (
    <ModalComponent
      open={open}
      setOpen={setOpen}
      onCloseCallback={() => {
        resetForm()
        onCloseCallback()
      }}
      title={
        ambassadorId
          ? `Edit ${
              !fetchingAmbassadorDetails
                ? (ambassadorDetails?.data?.ambassadorUserData?.firstname ??
                    '') +
                  ' ' +
                  (ambassadorDetails?.data?.ambassadorUserData?.lastname ?? '')
                : '...'
            }`
          : 'Add ambassador'
      }
      childrenClassName="md:!max-w-[420px]"
    >
      <form
        className="flex flex-col justify-center gap-4 relative"
        onSubmit={handleSubmit}
      >
        {fetchingAmbassadorDetails ? (
          <Spinner
            fullScreen={false}
            className="absolute inset-0 bg-white/50 z-10"
          />
        ) : undefined}

        <ComboBox
          isDropdown
          type="text"
          name="customer.name"
          value={values.customer.name}
          onChange={(e) => {
            handleChange(e)
            setFieldValue('customer.id', undefined)
          }}
          onBlur={handleBlur}
          hasIcon={false}
          //   title="Ambassador"
          placeholder="Select ambassador"
          errors={
            touched.customer?.name && errors.customer?.id
              ? errors.customer.id
              : ''
          }
          menuItemsClassName=""
          menuItems={
            customers?.data?.customers?.map((cust) => ({
              name: (
                <span>
                  <span className="capitalize">
                    {cust.firstname} {cust.lastname}
                  </span>{' '}
                  • {cust.email}
                </span>
              ),
              value: {
                name: `${cust.firstname} ${cust.lastname}`,
                phone: `${cust.phoneNumber}`,
                email: `${cust.email}`,
                id: cust._id,
              },
            })) ?? []
          }
          onSelectOption={(selected) => {
            setFieldValue('customer', {
              id: selected?.id,
              name: selected?.name,
            })
            setQuery({
              ...query,
              search: undefined,
            })
          }}
          onSearch={(val) => {
            setQuery({
              ...query,
              search: val === '' ? undefined : val,
            })
          }}
          loading={fetchingCustomers}
          immediate
          position="bottom start"
          menuClassName="!max-h-[250px] !z-[999] shadow-md"
          className={classNames('w-full', {
            '!hidden': ambassadorId,
          })}
        />

        {ambassadorId ? (
          <AccountDetails
            onChange={(details) => {
              setFieldValue('accountName', details.accountName)
              setFieldValue('accountNumber', details.accountNumber)
              setFieldValue('bankName', details.bankName)
              setFieldValue('bankCode', details.bankCode)
            }}
            formik={formik}
          />
        ) : undefined}

        <Button
          label={ambassadorId ? 'Save' : 'Add ambassador'}
          type="submit"
          className="mt-2 w-full"
          disabled={!isValid || creatingAmbassador || updatingAmbassador}
          loading={creatingAmbassador || updatingAmbassador}
          primary
        />
      </form>
    </ModalComponent>
  )
}
